import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatChannelMemberInfo,
  ChatMemberRoleEnum,
  ChatService,
  ChatUserDisplayMap,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs/operators';
import { resolveChatInitials } from '../../chat-initials.util';
import { ChatPresenceView } from '../../chat-status/chat-status.util';
import { presenceViewsSelector } from '../../state/chat.selectors';

/** Dialog input — which conversation to list. */
export interface ChatChannelMembersDialogData {
  channelId: string;
  /** Conversation name, shown in the subtitle. */
  channelName: string;
}

interface ChatMemberRow {
  id: string;
  name: string;
  image?: string;
  initial: string;
  /** Conversation-level ADMIN — gets the badge. */
  isAdmin: boolean;
  isSelf: boolean;
}

/**
 * Internal Chat "Participantes" dialog — opened by clicking the conversation
 * title in the pane header (PUBLIC/PRIVATE channels and GROUPs).
 *
 * Read-only roster over `GET /api/chat/channels/:id/members` (fetched fresh on
 * every open — invites/joins/leaves are frequent). Names/avatars come from the
 * endpoint's `meta.userDisplay`; presence + custom status feed the avatar dot
 * exactly like the invite dialog. Conversation-level ADMINs are badged and
 * listed first.
 */
@Component({
  templateUrl: './chat-channel-members-dialog.component.html',
  styleUrls: ['./chat-channel-members-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatChannelMembersDialogComponent extends AbstractComponent implements OnInit {

  rows: ChatMemberRow[] = [];
  filteredRows: ChatMemberRow[] = [];
  filterTerm = '';

  loadFailed = false;

  /** userId → presence view (dot class) for the avatar tiles. */
  private presenceViews: Record<string, ChatPresenceView> = {};

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatChannelMembersDialogComponent>,
    private readonly store: Store,
    private readonly chatService: ChatService,
    @Inject(MAT_DIALOG_DATA) public readonly data: ChatChannelMembersDialogData,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.store
      .select(presenceViewsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((views) => this.presenceViews = views);

    this.load();
  }

  load(): void {
    if (!this.data?.channelId) return;
    this.loading = true;
    this.loadFailed = false;

    this.chatService.getChannelMembers(this.data.channelId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (envelope) => {
          this.loading = false;
          this.rows = this.buildRows(envelope?.data ?? [], envelope?.meta?.userDisplay ?? {});
          this.applyFilter();
        },
        error: () => {
          this.loading = false;
          this.loadFailed = true;
        },
      });
  }

  applyFilter(): void {
    const term = this.filterTerm.trim().toLowerCase();
    this.filteredRows = term
      ? this.rows.filter((row) => row.name.toLowerCase().includes(term))
      : this.rows;
  }

  get memberCount(): number {
    return this.rows.length;
  }

  close(): void {
    this.dialogRef.close();
  }

  trackRow(_index: number, row: ChatMemberRow): string {
    return row.id;
  }

  /** Dot class for a member's avatar tile — same semantics as everywhere else. */
  dotFor(row: ChatMemberRow): string {
    return this.presenceViews[row.id]?.dotClass ?? 'bg-secondary';
  }

  /** Conversation ADMINs first, then alphabetical — self keeps its place. */
  private buildRows(members: ChatChannelMemberInfo[], userDisplay: ChatUserDisplayMap): ChatMemberRow[] {
    const currentUserId = this.getIDCurrentUser();

    return members
      .map((member) => {
        const display = userDisplay[member.user];
        const name = display?.name ?? '';
        return {
          id: member.user,
          name,
          image: display?.image,
          initial: resolveChatInitials(name || '?'),
          isAdmin: member.role === ChatMemberRoleEnum.ADMIN,
          isSelf: member.user === currentUserId,
        };
      })
      // A member whose appdb user vanished entirely has no name to show —
      // rare (userDisplay hydrates removed users too), so it is dropped.
      .filter((row) => !!row.name)
      .sort((a, b) => (a.isAdmin === b.isAdmin ? a.name.localeCompare(b.name) : (a.isAdmin ? -1 : 1)));
  }
}
